/* eslint-disable max-len */
/**
 * It converts geographical coordinates into a textual address representation using the TomTom
 * [Search API - CrossStreet lookup](CROSS_STREET_LOOKUP_URL).
 *
 * Parameters need to be passed to the constructor.
 *
 * ### Response
 * The final response from the service is a JSON object with the following structure:
 *
 * ```js
 * {
 *   address: {
 *     buildingNumber: 19,
 *     streetNumber: 19,
 *     street: 'N 2nd St',
 *     streetName: 'N 2nd St',
 *     crossStreet: 'E SANTA CLARA ST'
 *     streetNameAndNumber: '',
 *     speedLimit: '',
 *     countryCode: 'US",
 *     countrySubdivision: "'A',
 *     countrySecondarySubdivision: 'Santa Clara',
 *     countryTertiarySubdivision: '',
 *     municipality: 'San Jose',
 *     postalCode: '95113',
 *     municipalitySubdivision: "San Francisco Bay Area',
 *     freeformAddress: 'E SANTA CLARA ST &amp; N 2nd St, San Jose, CA, 95113'
 *   },
 *   position: {
 *     lng: -121.8898',
 *     lat: '37.337083
 *   }
 * }
 * ```
 *
 * The response is also extended with `getTrackingId()` method, which returns the `Tracking-ID`
 * associated with the request.
 *
 * Please refer to {{#crossLinkModule "Services"}}Difference between
 * API responses and this library's responses{{/crossLinkModule}} section.
 *
 * @example
 * ```js
 * tt.services.crossStreetLookup({
 *   key: <Your API key>,
 *   position: '19.449,51.759'
 * }).then(function(response) {
 *   console.log(response);
 * });
 * ```
 *
 * @class crossStreetLookup
 * @namespace Services.services
 * @module Services
 * @constructor
 * @uses RadiusMixin
 * @uses ProtocolMixin
 * @uses KeyMixin
 * @uses LimitMixin
 * @uses LanguageMixin
 * @uses PositionMixin
 * @uses HeadingMixin
 * @uses ViewMixin
 * @uses AllowFreeformNewlineMixin
 * @uses BatchMixin
 * @uses TrackingIdMixin
 * @uses AbortSignalMixin
 *
 * @param {Object} [options]
 * @param {Object} [additionalOptions] Additional options to be passed to the service.
 */
/* eslint-enable max-len */
import {reverseGeocodeBase} from './reverseGeocodeBase';
import {reverseGeocodeParams} from './reverseGeocodeParameters';
import {crossStreetLookupType} from './types';

export function crossStreetLookup(options, additionalOptions) {
    const ReverseGeocodeBasePrototype = reverseGeocodeBase.prototype.constructor;

    function CrossStreetLookup(options, additionalOptions) {
        ReverseGeocodeBasePrototype.call(this, options, additionalOptions);

        this.fields = reverseGeocodeParams(crossStreetLookupType);
    }

    CrossStreetLookup.prototype = new ReverseGeocodeBasePrototype();
    CrossStreetLookup.prototype.constructor = CrossStreetLookup;
    return (new CrossStreetLookup(options, additionalOptions)).construct('crossStreetLookup');
}
